import { useEffect, useId, useState } from 'react';
import { PanelLeft } from 'lucide-react';
import { Sidebar } from './nav/Sidebar';
import { HeaderUsuario } from './nav/HeaderUsuario';
import { Notificacoes } from './nav/Notificacoes';
import { useNarrow } from './nav/useNarrow';
import { findView, type ViewId } from './nav/nav-items';
import { Notice, ToastProvider } from './ui';
import { SessaoProvider, useEstadoSessao } from './lib/session';
import { Screen } from './screens';
import { Login } from './screens/Login';
import { ConsoleMaster } from './screens/ConsoleMaster';

function viewFromUrl(): ViewId {
  const requested = new URLSearchParams(window.location.search).get('view');
  return requested && findView(requested) ? requested as ViewId : 'inbox';
}
const conversationFromUrl = () => new URLSearchParams(window.location.search).get('conversation');

function Area() {
  const estado = useEstadoSessao();
  const [view, setView] = useState<ViewId>(viewFromUrl);
  const [conversation, setConversation] = useState<string | null>(conversationFromUrl);
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(false);
  const estreito = useNarrow();
  const navId = useId();
  const atual = findView(view);

  function navigate(next: ViewId, conversationId?: string) {
    setView(next);
    setConversation(conversationId ?? null);
    const url = new URL(window.location.href);
    if (next === 'inbox') url.searchParams.delete('view');
    else url.searchParams.set('view', next);
    if (conversationId && next === 'inbox') url.searchParams.set('conversation', conversationId);
    else url.searchParams.delete('conversation');
    window.history.pushState({ view:next }, '', url);
  }

  useEffect(() => { document.title = `${atual?.label ?? 'Setter WA'} · Setter WA`; }, [atual]);
  useEffect(() => {
    const restore = () => { setView(viewFromUrl()); setConversation(conversationFromUrl()); };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);

  if (estado.status === 'carregando') {
    return <p style={{ padding: 'var(--space-8)', color: 'var(--text-muted)' }} role="status">Verificando sua sessão…</p>;
  }
  // A entrada é uma tela do produto, não um redirecionamento para o front
  // anterior. Enquanto era `/login.html`, o `dev:web` quebrava toda vez que a
  // sessão expirava: esse arquivo é servido pelo Express na 3000, e o Vite
  // roda na 5173.
  if (estado.status === 'anonimo') return <Login />;
  // Sessão de plataforma: outra identidade, outro produto. Ver o
  // comentário em ConsoleMaster.tsx sobre por que não reusa o shell.
  if (estado.status === 'master') return <ConsoleMaster master={estado.master} />;
  if (estado.status === 'erro') {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <Notice tone="danger" title="Não foi possível iniciar" live><p>{estado.mensagem}</p></Notice>
      </div>
    );
  }

  const isAdmin = estado.sessao.agent.role === 'ADMIN';

  return (
    <>
      <a className="skip" href="#conteudo">Ir para o conteúdo</a>
      <div className="shell">
        <div className="topbar">
          <button
            type="button"
            className="topbar__menu"
            onClick={() => (estreito ? setOpen((v) => !v) : setCollapsed((v) => !v))}
            aria-expanded={estreito ? open : !collapsed}
            aria-controls={navId}
            aria-label={(estreito ? open : !collapsed) ? 'Recolher menu' : 'Expandir menu'}
          >
            <PanelLeft size={20} aria-hidden="true" />
          </button>
          <span className="topbar__title">{estado.sessao.tenant.name}</span>
          {/* Identidade e alertas à direita, fora da navegação. O perfil não é
              uma seção do produto: é de quem está usando. */}
          <div className="topbar__direita">
            <Notificacoes onAbrirSecao={navigate} />
            <HeaderUsuario onAbrirConfiguracoes={() => navigate('settings')} />
          </div>
        </div>

        <Sidebar
          id={navId}
          current={view}
          onNavigate={navigate}
          isAdmin={isAdmin}
          collapsed={collapsed}
          open={open}
          onClose={() => setOpen(false)}
        />

        <div className="content" data-view={view} style={{ minWidth: 0 }}>
          <main
            id="conteudo"
            className="workspace"
            tabIndex={-1}
            data-focus-silent
            style={{ padding: 'var(--space-6)', display: 'grid', gap: 'var(--space-6)', alignContent: 'start' }}
          >
            <header>
              <h1 style={{ fontSize: 'var(--text-2xl)' }}>{atual?.label ?? 'Setter WA'}</h1>
            </header>
            <Screen view={view} onNavigate={navigate} conversationId={conversation} />
          </main>
        </div>
      </div>
    </>
  );
}

export function App() {
  return (
    <SessaoProvider>
      <ToastProvider>
        <Area />
      </ToastProvider>
    </SessaoProvider>
  );
}
